import _ from "lodash";
import { call, put } from "redux-saga/effects";

import actions from "@/actions";
import { Api } from "@/utils";

function* fetchLightGroupSettings({ lightGroup }) {
    try {
        const path = `v1/control-group-settings/control-group-id/${lightGroup.groupID}`;
        return yield call(Api.get, path, {}, { parseErrorBody: true });
    } catch (e) {
        if (e.cause?.statusCode !== 404) {
            throw e;
        }
        return null;
    }
}

function* fetchAllLightGroupSettings({ lightGroups }) {
    const existingById = {};
    const missingIds = [];

    for (const group of lightGroups) {
        const settings = yield call(fetchLightGroupSettings, {
            lightGroup: group,
        });

        if (!settings) {
            missingIds.push(group.groupID);
            continue;
        }

        existingById[group.groupID] = settings;
    }

    return { existingById, missingIds };
}

function* createLightGroupSettings({
    missingIds,
    lightGroups,
    defaultLightGroups,
}) {
    if (!missingIds.length) {
        return {};
    }

    const payload = [];

    for (const groupID of missingIds) {
        const { displayID } = lightGroups.find(
            ({ groupID: id }) => id === groupID,
        );
        const defaultSettings = defaultLightGroups.find(
            ({ displayID: id }) => id === displayID,
        );
        payload.push({ groupID, ...defaultSettings });
    }

    const { result } = yield call(
        Api.put,
        "v1/control-group-settings/with-controller-overrides",
        payload,
    );

    return _.mapValues(_.groupBy(result, "groupID"), (settings) => settings[0]);
}

/**
 * Loads or creates light group settings for all groups, using defaults for any missing ones.
 *
 * @param root0 - The configuration options.
 * @param root0.lightGroups - The light groups to load settings for.
 * @param root0.defaultLightGroups - The default light groups for fallback.
 * @yields Calls the API and dispatches light group settings.
 */
function* loadLightGroupSettings({ lightGroups, defaultLightGroups }) {
    try {
        const { existingById, missingIds } = yield call(
            fetchAllLightGroupSettings,
            { lightGroups },
        );
        const newById = yield call(createLightGroupSettings, {
            missingIds,
            lightGroups,
            defaultLightGroups,
        });
        const settingsById = { ...existingById, ...newById };

        yield put(actions.map.general.setLightGroupSettings(settingsById));
    } catch (e) {
        // eslint-disable-next-line no-console
        console.log("error loading light group settings: ", e);
    }
}

export default loadLightGroupSettings;
